/**
 * Callback date + time in Pakistan time (UTC+5, no daylight saving).
 * The picker works in Karachi wall time ("2026-10-19" + "15:30"); the API
 * gets a UTC ISO instant.
 */
const PKT_OFFSET_MIN = 5 * 60

export interface WallTime {
  /** YYYY-MM-DD (Karachi calendar day) */
  ymd: string
  /** HH:mm 24h */
  hm: string
}

export interface CallingWindow {
  callingStartMinute: number
  callingEndMinute: number
  /** 0 = Sunday … 6 = Saturday */
  callingDays: number[]
}

const pad = (n: number) => String(n).padStart(2, '0')

/** Karachi wall time of an instant. */
export function toWallTime(date: Date): WallTime {
  const shifted = new Date(date.getTime() + PKT_OFFSET_MIN * 60_000)
  return {
    ymd: `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`,
    hm: `${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`,
  }
}

/** UTC ISO for a Karachi wall time; null when incomplete or invalid. */
export function wallTimeToIso({ ymd, hm }: Partial<WallTime>): string | null {
  if (!ymd || !hm || !/^\d{4}-\d{2}-\d{2}$/.test(ymd) || !/^\d{2}:\d{2}$/.test(hm)) return null
  const date = new Date(`${ymd}T${hm}:00+05:00`)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

export function minuteToHm(minute: number): string {
  return `${pad(Math.floor(minute / 60))}:${pad(minute % 60)}`
}

function hmToMinute(hm: string): number {
  const [h = 0, m = 0] = hm.split(':').map(Number)
  return h * 60 + m
}

/** Karachi weekday (0 = Sun) for a YYYY-MM-DD. */
function weekdayOf(ymd: string): number {
  return new Date(`${ymd}T12:00:00Z`).getUTCDay()
}

function addDays(ymd: string, days: number): string {
  const d = new Date(`${ymd}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

/** Is this Karachi wall time inside calling hours? */
export function isInsideWindow(wall: WallTime, window: CallingWindow): boolean {
  if (!window.callingDays.includes(weekdayOf(wall.ymd))) return false
  const minute = hmToMinute(wall.hm)
  return minute >= window.callingStartMinute && minute < window.callingEndMinute
}

/** First calling day strictly after `ymd`. */
function nextCallingDay(ymd: string, window: CallingWindow): string {
  for (let i = 1; i <= 7; i += 1) {
    const candidate = addDays(ymd, i)
    if (window.callingDays.includes(weekdayOf(candidate))) return candidate
  }
  return addDays(ymd, 1)
}

export type QuickRetryKey = 'in_1h' | 'later_today' | 'tomorrow'

export interface QuickRetry {
  key: QuickRetryKey
  label: string
  wall: WallTime
}

const LATER_TODAY_MINUTE = 17 * 60
const TOMORROW_MINUTE = 10 * 60

/**
 * Quick retry picks for No answer / Busy / Dropped. Only picks that land
 * inside calling hours are offered (a 19:30 "in 1 hour" would be 20:30).
 */
export function quickRetries(now: Date, window: CallingWindow): QuickRetry[] {
  const today = toWallTime(now)
  const picks: QuickRetry[] = []

  // Round up to the next 5 minutes so the time reads cleanly.
  const inOneHour = new Date(Math.ceil((now.getTime() + 60 * 60_000) / 300_000) * 300_000)
  const oneHour = toWallTime(inOneHour)
  if (isInsideWindow(oneHour, window)) picks.push({ key: 'in_1h', label: '1 hour', wall: oneHour })

  // Before 16:00 only, so "1 hour" and "Later today" never land on the same time.
  const laterToday = { ymd: today.ymd, hm: minuteToHm(LATER_TODAY_MINUTE) }
  if (hmToMinute(today.hm) < LATER_TODAY_MINUTE - 60 && isInsideWindow(laterToday, window)) {
    picks.push({ key: 'later_today', label: 'Later today', wall: laterToday })
  }

  const startMinute = Math.max(TOMORROW_MINUTE, window.callingStartMinute)
  picks.push({
    key: 'tomorrow',
    label: weekdayOf(nextCallingDay(today.ymd, window)) === weekdayOf(addDays(today.ymd, 1)) ? 'Tomorrow' : 'Next day',
    wall: {
      ymd: nextCallingDay(today.ymd, window),
      hm: minuteToHm(startMinute),
    },
  })
  return picks
}
